import React, { useState, useMemo, useRef, useEffect, Fragment } from "react";
import "../../../css/timelineDaily.css";
import { parseTime } from "../../../utils/dateHelpers";

/* ------------------------------------------------------------------ */
/* Costanti e helper                                                  */
/* ------------------------------------------------------------------ */
const daysIT   = ["domenica","lunedì","martedì","mercoledì","giovedì","venerdì","sabato"];
const normalize = s => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Normalizza qualsiasi formato data a YYYY-MM-DD
const toYMD = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return value.includes('T') ? value.slice(0, 10) : value;
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'object') {
    if (typeof value.toISOString === 'function') {
      return value.toISOString().slice(0, 10);
    }
  }
  return '';
};
const dayMap = {
  lunedi: 1, "lunedì": 1,
  martedi: 2, "martedì": 2,
  mercoledi: 3, "mercoledì": 3,
  giovedi: 4, "giovedì": 4,
  venerdi: 5, "venerdì": 5,
  sabato: 6,
  domenica: 0,
};

const MS_IN_DAY        = 86_400_000;
const MIN_PADDING_PCT  = 0;
const MIN_SLOT_WIDTH_PCT = 0.2;
const EDGE_PADDING_MS  = 30 * 60_000;  // 30 minuti

/* ------------------------------------------------------------------ */
/* Componente                                                         */
/* ------------------------------------------------------------------ */
export default function TimelineDaily({ schedules = [], programs = [], now, onSelect, onDayChange }) {
  const [selectedDate, setSelectedDate] = useState(new Date(now || Date.now()));
  const [nowState, setNowState]         = useState(now || new Date());
  const nowUsed = now || nowState;
  const getSlotsForSelectedDay = sch => {
    if (!sch) return { slots: [], occurrence: null };
    const occurrences = sch.occurrences || [];
    const baseSlots = sch.timeSlots || [];
    const key = toYMD(selectedDate);
    const match = occurrences.find(occ => toYMD(occ.date) === key);
    const slots = match?.timeSlots?.length ? match.timeSlots : baseSlots;
    return { slots, occurrence: match || null };
  };

  /* aggiorna l’orologio reale ogni minuto se “now” non è forzato dall’esterno */
  useEffect(() => {
    if (!now) {
      const t = setInterval(() => setNowState(new Date()), 60_000);
      return () => clearInterval(t);
    }
  }, [now]);

  /* ------------------------------------------------------------------ */
  /* Cambio giorno / data                                               */
  /* ------------------------------------------------------------------ */
  const containerRef = useRef(null);
  const shiftDay = d => setSelectedDate(prev => {
    const n = new Date(prev);
    n.setDate(n.getDate() + d);
    return n;
  });
  const handlePick = e => e.target.value && setSelectedDate(new Date(e.target.value));

  useEffect(() => {                      
    onDayChange?.(selectedDate);
  }, [selectedDate, onDayChange]);

  /* ------------------------------------------------------------------ */
  /* Schedules del giorno + risoluzione conflitti per priority          */
  /* ------------------------------------------------------------------ */
  const schedulesOfDay = useMemo(() => {
    const dateKey = toYMD(selectedDate);

    // Helper per ottenere orari effettivi (dall'occorrenza se presente, altrimenti dal template)
    const getEffectiveTimes = (sch) => {
      if (sch.isRecurring && sch.occurrences?.length) {
        // Usa toYMD per normalizzare entrambe le date prima del confronto
        const occ = sch.occurrences.find(o => toYMD(o.date) === dateKey);
        if (occ) {
          return { startTime: occ.startTime, endTime: occ.endTime, occurrence: occ };
        }
      }
      return { startTime: sch.startTime, endTime: sch.endTime, occurrence: null };
    };

    // Helper: verifica se una data è nella blacklist
    const isDateBlacklisted = (sch, date) => {
      if (!sch.blacklistDates || !Array.isArray(sch.blacklistDates)) return false;
      const dateStr = toYMD(date);
      return sch.blacklistDates.some(b => {
        const blacklistDate = b.blacklistDate || b.blacklist_date || b;
        return toYMD(blacklistDate) === dateStr;
      });
    };

    // 1. filtra per il giorno corrente
    const dayList = schedules
      .filter(s => {
        const isRec = s.isRecurring && s.dayOfWeek &&
                      dayMap[normalize(s.dayOfWeek)] === selectedDate.getDay() &&
                      !isDateBlacklisted(s, selectedDate);
        const isShot = s.date &&
                       new Date(s.date).toDateString() === selectedDate.toDateString();
        return isRec || isShot;
      })
      .map(sch => {
        // Arricchisci con i dati effettivi dell'occorrenza se presente
        const effective = getEffectiveTimes(sch);
        return {
          ...sch,
          startTime: effective.startTime,
          endTime: effective.endTime,
          _occurrence: effective.occurrence, // Mantieni riferimento all'occorrenza
          _originalStartTime: sch.startTime, // Mantieni originale per riferimento
          _originalEndTime: sch.endTime,
        };
      })
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

    // 2. elimina conflitti tenendo quello con priority più alta
    const resolved = [];
    dayList.forEach(sch => {
      const sStart = parseTime(sch.startTime, selectedDate).getTime();
      const sEnd   = parseTime(sch.endTime,   selectedDate).getTime();

      let replaced = false;
      for (let i = 0; i < resolved.length; i++) {
        const r       = resolved[i];
        const rStart  = parseTime(r.startTime, selectedDate).getTime();
        const rEnd    = parseTime(r.endTime,   selectedDate).getTime();

        // sovrapposizione?
        if (sStart < rEnd && sEnd > rStart) {
          if ((sch.priority ?? 0) > (r.priority ?? 0)) resolved[i] = sch;
          replaced = true;
          break;
        }
      }
      if (!replaced) resolved.push(sch);
    });

    return resolved.sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [schedules, selectedDate]);

  /* ------------------------------------------------------------------ */
  /* Calcoli di timeline                                                */
  /* ------------------------------------------------------------------ */
  const startOfDay = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [selectedDate]);

  const { minTime, timeSpan } = useMemo(() => {
    if (!schedulesOfDay.length) {
      return { minTime: startOfDay.getTime(), timeSpan: MS_IN_DAY };
    }
    const times = schedulesOfDay.flatMap(s => [
      parseTime(s.startTime, startOfDay).getTime(),
      parseTime(s.endTime,   startOfDay).getTime(),
    ]);
    const earliest = Math.min(...times) - EDGE_PADDING_MS;
    const latestUnpadded = Math.max(...times);
    const latest = Math.min(
      latestUnpadded + EDGE_PADDING_MS,
      startOfDay.getTime() + MS_IN_DAY
    );
    const minT = Math.max(earliest, startOfDay.getTime());
    return { minTime: minT, timeSpan: latest - minT };
  }, [schedulesOfDay, startOfDay]);

  const positioned = useMemo(() => {
    if (!schedulesOfDay.length) return [];

    // caso singolo -> centriamo
    if (schedulesOfDay.length === 1) {
      const sch = schedulesOfDay[0];
      const sMs = parseTime(sch.startTime, startOfDay).getTime();
      const eMs = parseTime(sch.endTime,   startOfDay).getTime();
      const durP   = Math.max(((eMs - sMs) / timeSpan) * 100, MIN_SLOT_WIDTH_PCT);
      const startP = (100 - durP) / 2;
      const liveP  = nowUsed >= sMs && nowUsed <= eMs
        ? ((nowUsed - sMs) / (eMs - sMs)) * 100
        : 0;
      return [{ sch, adjStart: startP, adjDur: durP, livePct: liveP }];
    }

    // più slot -> algoritmo di compressione
    const arr = [];
    schedulesOfDay.forEach(sch => {
      const sMs = parseTime(sch.startTime, startOfDay).getTime();
      const eMs = parseTime(sch.endTime,   startOfDay).getTime();
      const rawStart = ((sMs - minTime) / timeSpan) * 100;
      const rawDur   = Math.max(((eMs - sMs) / timeSpan) * 100, MIN_SLOT_WIDTH_PCT);

      let adjStart = Math.max(rawStart, MIN_PADDING_PCT);
      if (arr.length) {
        const prevEnd = arr[arr.length - 1].adjStart + arr[arr.length - 1].adjDur;
        if (adjStart < prevEnd + MIN_PADDING_PCT) adjStart = prevEnd + MIN_PADDING_PCT;
      }
      adjStart = Math.min(adjStart, 100 - MIN_PADDING_PCT - rawDur);

      const livePct = nowUsed >= sMs && nowUsed <= eMs
        ? ((nowUsed - sMs) / (eMs - sMs)) * 100
        : 0;

      arr.push({ sch, adjStart, adjDur: rawDur, livePct });
    });
    return arr;
  }, [schedulesOfDay, minTime, timeSpan, startOfDay, nowUsed]);

  const stateFor = sch => {
    if (selectedDate.toDateString() !== nowUsed.toDateString()) {
      return selectedDate < nowUsed ? "past" : "upcoming";
    }
    const s = parseTime(sch.startTime, startOfDay);
    const e = parseTime(sch.endTime,   startOfDay);
    if (nowUsed >= s && nowUsed <= e) return "live";
    return nowUsed > e ? "past" : "upcoming";
  };

  /* ------------------------------------------------------------------ */
  /* Click su un punto della timeline                                   */
  /* ------------------------------------------------------------------ */
  const handleSelect = (sch, pct) => {
    const prog = programs.find(p => String(p.id) === String(sch.programId)) || {};
    // Usa l'occorrenza già calcolata in schedulesOfDay (_occurrence)
    // invece di cercare di nuovo nelle occurrences originali
    const occurrence = sch._occurrence || null;
    const activeSlots = occurrence?.timeSlots?.length
      ? occurrence.timeSlots
      : (sch.timeSlots || []);

    const payload = {
      ...sch,
      programData: prog,
      activeSlots,
      activeOccurrence: occurrence,
      // Aggiungi nota occorrenza se presente
      occurrenceNote: occurrence?.note || null,
    };
    onSelect?.(payload);

    if (containerRef.current) {
      containerRef.current.scrollTo({
        left: Math.max(
          (pct / 100) * containerRef.current.scrollWidth -
          containerRef.current.clientWidth / 2,
          0
        ),
        behavior: "smooth",
      });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */
  return (
    <>
      {/* ---------- header e navigazione data ---------- */}
      <div className="timeline-wrapper">
        <div className="timeline-header">
          <button className="timeline-nav-btn" onClick={() => shiftDay(-1)}>❮</button>

          <span>
            {daysIT[selectedDate.getDay()]} {selectedDate.getDate()}{" "}
            {selectedDate.toLocaleDateString("it-IT", { month: "short" })}
          </span>

          <button className="timeline-nav-btn" onClick={() => shiftDay(1)}>❯</button>

          <label className="timeline-calendar-btn" title="scegli data">
            <i className="fa fa-calendar-o" />
            <input
              type="date"
              value={selectedDate.toISOString().slice(0, 10)}
              onChange={handlePick}
            />
          </label>
        </div>

        {/* ---------- barra oraria ---------- */}
        <div className="timeline-daily-container" ref={containerRef}>
          <div className="timeline-daily-bar-bg" />

          {positioned.map(({ sch, adjStart, adjDur, livePct }, idx) => {
            const prog = programs.find(p => String(p.id) === String(sch.programId)) || {};
            const state     = stateFor(sch);
            const nextSch   = positioned[idx + 1]?.sch;
            const hideEndLbl = nextSch && nextSch.startTime === sch.endTime;

            // Determina se l'item è vicino ai bordi per il tooltip
            const edgeClass = adjStart < 12 ? 'edge-left' : adjStart > 88 ? 'edge-right' : '';

            return (
              <Fragment key={sch.id || `${idx}-${sch.startTime}`}>
                {/* range colorato */}
                <div
                  className={`timeline-range timeline-${state}`}
                  style={{ left: `${adjStart}%`, width: `${adjDur}%` }}
                >
                  {state === "live" && (
                    <div
                      className="timeline-range-live-fill"
                      style={{ width: `${livePct}%` }}
                    />
                  )}
                </div>

                {/* puntino + label inizio */}
                <div
                  className={`timeline-item ${edgeClass}`}
                  style={{ left: `${adjStart}%` }}
                  onClick={() => handleSelect(sch, adjStart)}
                >
                  <div className="timeline-dot" />
                  <div className="timeline-label">
                    {sch.startTime.slice(0, 5)}
                  </div>
                  <img
                    src={prog.imageUrl || prog.image_url || "https://via.placeholder.com/28"}
                    alt={prog.title}
                    className="timeline-thumb"
                  />
                  <div className="timeline-tooltip">
                    <strong>{prog.title}</strong><br />
                    {sch.startTime.slice(0, 5)} – {sch.endTime.slice(0, 5)}
                    <br />P{sch.priority}
                  </div>
                </div>

                {/* label / freccia fine */}
                <div
                  className="timeline-item-end"
                  style={{ left: `${adjStart + adjDur}%` }}
                >
                  {!hideEndLbl && (
                    <div className="timeline-label timeline-label-end">
                      {sch.endTime.slice(0, 5)}
                    </div>
                  )}
                  <div className="timeline-end-arrow" />
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
}
